import {useMemo, useState} from "react";
import {SettlementEntity} from "@ots-frontend/ots-data";
import {
  DataTableFilterConfig,
  useDataTableFilter,
  UseDataTableFilterProps,
  UseDataTableFilterResult
} from "../DataTable/useDataTableFilter";
import {useTranslation} from "react-i18next";
import {useDebouncedValue} from "@mantine/hooks";
import {getFormattedDatabaseCode} from "../../text/FormattedDatabaseCode/FormattedDatabaseCode";

export type UseSettlementDataTableFilterProps = Omit<UseDataTableFilterProps<SettlementEntity>, 'filterConfig'>

export function useSettlementDataTableFilter(props: UseSettlementDataTableFilterProps): UseDataTableFilterResult<SettlementEntity> {
  const {records} = props;

  const {t} = useTranslation(["common"])

  const [statusQuery, setStatusQuery] = useState<string | undefined>(undefined);
  const [statusDebouncedQuery] = useDebouncedValue(statusQuery, 200);

  const [nameQuery, setNameQuery] = useState<string | undefined>(undefined);
  const [nameDebouncedQuery] = useDebouncedValue(nameQuery, 200);

  const [periodQuery, setPeriodQuery] = useState<string | undefined>(undefined);
  const [periodDebouncedQuery] = useDebouncedValue(periodQuery, 200);

  const [categoryQuery, setCategoryQuery] = useState<string | undefined>(undefined);
  const [categoryDebouncedTypeQuery] = useDebouncedValue(categoryQuery, 200);

  const [companyQuery, setCompanyQuery] = useState<string | undefined>(undefined);
  const [companyDebouncedQuery] = useDebouncedValue(companyQuery, 200);

  const [companyUnitQuery, setCompanyUnitQuery] = useState<string | undefined>(undefined);
  const [companyUnitDebouncedQuery] = useDebouncedValue(companyUnitQuery, 200);

  const filterConfig: DataTableFilterConfig<SettlementEntity> = useMemo(() => {
    return {
      "status": {
        setQuery: setStatusQuery,
        query: statusQuery,
        debouncedQuery: statusDebouncedQuery,
        filter: (record, query) => {
          const display = getFormattedDatabaseCode(record.status, t)
          return !record.status.code.toLowerCase().includes(query.toLowerCase()) && (!display || !display.toLowerCase().includes(query.toLowerCase()))
        }
      },
      "name": {
        setQuery: setNameQuery,
        query: nameQuery,
        debouncedQuery: nameDebouncedQuery,
        filter: (record, query) => !record.name || !record.name.toLowerCase().includes(query.toLowerCase())
      },
      "period": {
        setQuery: setPeriodQuery,
        query: periodQuery,
        debouncedQuery: periodDebouncedQuery,
        filter: (record, query) => {
          const display = getFormattedDatabaseCode(record.activityPeriodType, t)
          return !record.activityPeriodType || (!record.activityPeriodType.code.toLowerCase().includes(query.toLowerCase()) && (!display || !display.toLowerCase().includes(query.toLowerCase())))
        }
      },
      "category": {
        setQuery: setCategoryQuery,
        query: categoryQuery,
        debouncedQuery: categoryDebouncedTypeQuery,
        filter: (record, query) => {
          const display = getFormattedDatabaseCode(record.activityPeriodCategory, t)
          return !record.activityPeriodType || (!record.activityPeriodType.code.toLowerCase().includes(query.toLowerCase()) && (!display || !display.toLowerCase().includes(query.toLowerCase())))
        }
      },
      "company": {
        setQuery: setCompanyQuery,
        query: companyQuery,
        debouncedQuery: companyDebouncedQuery,
        filter: (record, query) => !record.activityPeriodCompanyName || !record.activityPeriodCompanyName.toLowerCase().includes(query.toLowerCase())
      },
      "companyUnit": {
        setQuery: setCompanyUnitQuery,
        query: companyUnitQuery,
        debouncedQuery: companyUnitDebouncedQuery,
        filter: (record, query) => !record.activityPeriodCompanyUnitName || !record.activityPeriodCompanyUnitName.toLowerCase().includes(query.toLowerCase())
      },
    }
  }, [
    statusQuery,
    statusDebouncedQuery,
    nameQuery,
    nameDebouncedQuery,
    periodQuery,
    periodDebouncedQuery,
    categoryQuery,
    categoryDebouncedTypeQuery,
    companyQuery,
    companyDebouncedQuery,
    companyUnitQuery,
    companyUnitDebouncedQuery,
    t
  ])

  const {filteredRecords} = useDataTableFilter({
    records: records,
    filterConfig: filterConfig,
  })

  return {
    filteredRecords: filteredRecords,
    filterConfig: filterConfig
  };
}
